import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const COP = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n)||0);

// ── Parser CSV Davivienda P2M (pipe-delimited) ───────────
function parsearCSVDaviviendaP2M(lineas) {
  const headers = lineas[0].split('|').map(h => h.trim());
  const idx = (n) => headers.findIndex(h => h.toLowerCase().includes(n.toLowerCase()));
  const iFranq  = idx('FRANQUICIA');
  const iFecha  = idx('FECHA DE CONSUMO');
  const iCompra = idx('VALOR COMPRA');
  const iComis  = idx('VALOR COMISION');
  const iIdTrx  = idx('ID transacci');  // ID transacción — único por operación
  const FRANQ_MAP = { 'P2M': 'qr', 'VISA': 'visa', 'MASTERCARD': 'mastercard', 'MC': 'mastercard', 'AMEX': 'amex', 'AMERICAN': 'amex' };
  const movimientos = [];
  for (let i = 1; i < lineas.length; i++) {
    const cols = lineas[i].split('|');
    if (cols.length < 5) continue;
    const franq = cols[iFranq]?.trim().toUpperCase();
    const tipo = Object.entries(FRANQ_MAP).find(([k]) => franq?.includes(k))?.[1];
    if (!tipo) continue;
    const fechaRaw = cols[iFecha]?.trim();
    if (!fechaRaw || fechaRaw.length !== 8) continue;
    const fecha = `${fechaRaw.slice(0,4)}-${fechaRaw.slice(4,6)}-${fechaRaw.slice(6,8)}`;
    const monto = parseInt(cols[iCompra]?.trim()) || 0;
    const comision = parseInt(cols[iComis]?.trim()) || 0;
    if (monto <= 0) continue;
    const id_trx = iIdTrx >= 0 ? cols[iIdTrx]?.trim() : null;
    movimientos.push({ fecha, descripcion: `P2M ${franq}`, tipo, monto, comision, franquicia: franq, id_trx });
  }
  return movimientos;
}

// ── Parser CSV Credibanco (semicolon, tarjetas) ───────────
function parsearCSVCredibanco(lineas) {
  const parseCsv = (linea) => linea.split(';').map(c => c.trim().replace(/^"|"$/g, ''));
  const headers = parseCsv(lineas[0]);
  const idx = (n) => headers.findIndex(h => h.toLowerCase().includes(n.toLowerCase()));
  const iFranq  = idx('Franquicia');
  const iFecha  = idx('Fecha');
  const iValor  = idx('Valor Compra');
  const iEstado = idx('Estado transacci');
  const FRANQ_MAP = { 'VISA': 'visa', 'MASTERCARD': 'mastercard', 'AMEX': 'amex', 'AMERICAN': 'amex', 'DINERS': 'amex' };
  const movimientos = [];
  for (let i = 1; i < lineas.length; i++) {
    const cols = parseCsv(lineas[i]);
    if (cols.length < 5) continue;
    const estado = cols[iEstado]?.trim();
    if (estado !== 'Aprobada') continue;
    const franq = cols[iFranq]?.trim().toUpperCase();
    const tipo = Object.entries(FRANQ_MAP).find(([k]) => franq?.includes(k))?.[1] || 'visa';
    const fechaRaw = cols[iFecha]?.trim();
    if (!fechaRaw) continue;
    const fecha = fechaRaw.slice(0,10).replace(/\//g, '-');
    const monto = parseInt(cols[iValor]?.trim()) || 0;
    if (monto <= 0) continue;
    movimientos.push({ fecha, descripcion: `Credibanco ${franq}`, tipo, monto, comision: 0, franquicia: franq });
  }
  return movimientos;
}

// ── Auto-detecta formato ──────────────────────────────────
function parsearCSVDatafono(texto) {
  const lineas = texto.split('\n').filter(l => l.trim());
  if (!lineas.length) return [];
  if (lineas[0].includes('|')) return parsearCSVDaviviendaP2M(lineas);
  if (lineas[0].includes(';')) return parsearCSVCredibanco(lineas);
  return [];
}


const st2 = {
  label: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  input: { padding: '8px 12px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', background: 'white' },
};

// ── Vista datáfono ────────────────────────────────────────
function VistaDatafono({ bodega, datosDf, previewDf, procesandoDf, importandoDf, cargandoDf,
  desdeDf, setDesdeDf, hastaDf, setHastaDf, cargarDatafono, handleCSVDatafono, handleImportarDatafono, setPreviewDf, csvDfRef, modoQr }) {
  const modo = modoQr ? 'qr' : 'tarjeta';
  return (
    <div>
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#374151' }}>
          {modoQr ? '📱 QR / P2M' : '💳 Datáfono Credibanco'} — {bodega.nombre}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={st2.label}>Desde</div>
            <input type="date" value={desdeDf} onChange={e => setDesdeDf(e.target.value)} style={st2.input} />
          </div>
          <div>
            <div style={st2.label}>Hasta</div>
            <input type="date" value={hastaDf} onChange={e => setHastaDf(e.target.value)} style={st2.input} />
          </div>
          <button onClick={cargarDatafono} disabled={cargandoDf} style={{
            padding: '9px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer'
          }}>{cargandoDf ? '⏳' : '🔍 Consultar'}</button>
          <label style={{
            padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, display: 'inline-block',
            cursor: procesandoDf ? 'not-allowed' : 'pointer',
            background: procesandoDf ? '#E5E7EB' : 'linear-gradient(135deg,#0369A1,#0284C7)',
            color: procesandoDf ? '#9CA3AF' : 'white'
          }}>
            {procesandoDf ? '⏳ Procesando...' : modoQr ? '📂 Subir CSV Davivienda P2M' : '📂 Subir CSV Credibanco'}
            <input ref={csvDfRef} type="file" accept=".csv,.CSV" onChange={handleCSVDatafono}
              style={{ display: 'none' }} disabled={procesandoDf} multiple />
          </label>
        </div>
        {previewDf && (
          <div style={{ marginTop: 14, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1E40AF', marginBottom: 8 }}>
              {previewDf.transacciones.length} transacciones detectadas
              {previewDf.resumen?.archivos > 1 && (
                <span style={{ fontWeight: 400, color: '#6B7280', marginLeft: 8 }}>({previewDf.resumen.archivos} archivos)</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              {[
                { label: '💳 Tarjetas', val: previewDf.resumen.tarjeta, color: '#2563EB' },
                { label: '📱 QR / P2M', val: previewDf.resumen.qr, color: '#0EA5E9' },
                { label: 'Comisión banco', val: previewDf.resumen.comision, color: '#EF4444' },
              ].filter(x => x.val > 0).map((x, i) => (
                <div key={i} style={{ background: 'white', borderRadius: 8, padding: '7px 12px', fontSize: 12 }}>
                  <span style={{ color: '#6B7280' }}>{x.label}: </span>
                  <strong style={{ color: x.color, fontFamily: 'monospace' }}>{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(x.val)}</strong>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleImportarDatafono} disabled={importandoDf} style={{
                padding: '8px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                background: 'linear-gradient(135deg,#059669,#047857)', color: 'white', cursor: 'pointer'
              }}>{importandoDf ? '⏳ Importando...' : '💾 Confirmar e importar'}</button>
              <button onClick={() => setPreviewDf(null)} style={{
                padding: '8px 16px', border: '2px solid #E5E7EB', borderRadius: 8,
                background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontWeight: 600
              }}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
      {datosDf && datosDf.dias.length === 0 && (
        <div style={{ background: 'white', borderRadius: 14, padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
          Sin datos para este rango. Sube el CSV del datáfono primero.
        </div>
      )}
      {datosDf && datosDf.dias.length > 0 && (
        <TablaDatafono dias={datosDf.dias} totales={datosDf.totales} modo={modo} />
      )}
    </div>
  );
}

function TablaDatafono({ dias, totales, modo }) {
  // modo: 'tarjeta' | 'qr'
  const COP2 = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n)||0);
  const esTarjeta = modo !== 'qr';
  const color = esTarjeta ? '#2563EB' : '#0369A1';
  const titulo = esTarjeta ? '💳 Credibanco vs Sistema (Tarjeta)' : '📱 P2M / QR vs Sistema';
  return (
    <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
      <div style={{ background: color, padding: '11px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>{titulo}</span>
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>Comparación bruta sin deducir comisión</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Fecha',
                esTarjeta ? '# Trx tarjeta sis.' : '# Pagos QR sistema',
                esTarjeta ? '# Trx banco' : '# Trx P2M banco',
                esTarjeta ? 'Tarjeta sistema' : 'QR sistema (Nequi/etc)',
                esTarjeta ? 'Banco tarjeta' : 'Banco QR (P2M)',
                'Diferencia'].map(h => (
                <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Fecha' ? 'left' : 'right', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dias.map((d, i) => {
              const sisMonto  = esTarjeta ? d.sistema.tarjeta : d.sistema.qr;
              const bancoMonto = esTarjeta ? d.banco.tarjeta : d.banco.qr;
              const dif       = esTarjeta ? d.diferencia.tarjeta : d.diferencia.qr;
              const cuadra    = esTarjeta ? d.diferencia.cuadra_tarjeta : d.diferencia.cuadra_qr;
              const sisTrx    = esTarjeta ? d.sistema.trx_tarjeta : d.sistema.trx_qr;
              const alerta    = !cuadra && (sisMonto > 0 || bancoMonto > 0);
              const alertaTrx = !d.diferencia.cuadra_trx && d.banco.trx > 0;
              return (
                <tr key={d.fecha} style={{ borderTop: '1px solid #F3F4F6', background: alerta ? '#FFFBEB' : i%2===0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '9px 12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {new Date(d.fecha+'T12:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
                    <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>
                      {new Date(d.fecha+'T12:00').toLocaleDateString('es-CO', { weekday: 'long' })}
                    </div>
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color: alertaTrx ? '#EF4444' : '#6B7280', fontWeight: alertaTrx ? 800 : 400 }}>
                    {sisTrx || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color: alertaTrx ? '#EF4444' : color, fontWeight: 700 }}>
                    {d.banco.trx || '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#374151', fontWeight: 700 }}>
                    {sisMonto > 0 ? COP2(sisMonto) : '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color, fontWeight: 800 }}>
                    {bancoMonto > 0 ? COP2(bancoMonto) : '—'}
                    {esTarjeta && d.banco.detalle?.filter(x=>x.tipo!=='qr').map((x,j) => (
                      <div key={j} style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>{x.franquicia} {x.trx}trx</div>
                    ))}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                    <DifBadge val={dif} cuadra={cuadra} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          {totales && (
            <tfoot>
              <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F9FAFB' }}>
                <td style={{ padding: '10px 12px', fontWeight: 800, fontSize: 13 }}>Total</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#6B7280' }}>
                  {esTarjeta ? totales.sis_trx : totales.sis_trx}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color }}>{totales.banco_trx}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#374151' }}>
                  {COP2(esTarjeta ? totales.sis_tarjeta : totales.sis_qr)}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color }}>
                  {COP2(esTarjeta ? totales.banco_tarjeta : totales.banco_qr)}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <DifBadge
                    val={esTarjeta ? totales.banco_tarjeta - totales.sis_tarjeta : totales.banco_qr - totales.sis_qr}
                    cuadra={Math.abs(esTarjeta ? totales.banco_tarjeta - totales.sis_tarjeta : totales.banco_qr - totales.sis_qr) < 10000}
                  />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
        {dias.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
            Sin datos para este rango. Sube el CSV primero.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Vista Rappi ───────────────────────────────────────────
function VistaRappi({ bodega, datosRappi, previewRappi, procesandoRappi, importandoRappi,
  cargandoRappi, desdeRappi, setDesdeRappi, hastaRappi, setHastaRappi,
  cargarRappi, handleExcelRappi, handleImportarRappi, setPreviewRappi, csvRappiRef }) {
  const COP2 = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n)||0);
  const NARANJA = '#EA580C';
  return (
    <div>
      {/* Controles */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#374151' }}>
          🛵 Cuadre Rappi — {bodega.nombre}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={st2.label}>Desde</div>
            <input type="date" value={desdeRappi} onChange={e => setDesdeRappi(e.target.value)} style={st2.input} />
          </div>
          <div>
            <div style={st2.label}>Hasta</div>
            <input type="date" value={hastaRappi} onChange={e => setHastaRappi(e.target.value)} style={st2.input} />
          </div>
          <button onClick={cargarRappi} disabled={cargandoRappi} style={{
            padding: '9px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer'
          }}>{cargandoRappi ? '⏳' : '🔍 Consultar'}</button>
          <label style={{
            padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, display: 'inline-block',
            cursor: procesandoRappi ? 'not-allowed' : 'pointer',
            background: procesandoRappi ? '#E5E7EB' : `linear-gradient(135deg,${NARANJA},#C2410C)`,
            color: procesandoRappi ? '#9CA3AF' : 'white'
          }}>
            {procesandoRappi ? '⏳ Procesando...' : '📂 Subir reporte Rappi'}
            <input ref={csvRappiRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelRappi}
              style={{ display: 'none' }} disabled={procesandoRappi} />
          </label>
        </div>

        {/* Preview */}
        {previewRappi && (
          <div style={{ marginTop: 14, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#9A3412', marginBottom: 8 }}>
              🛵 {previewRappi.ordenes.length} órdenes detectadas — {COP2(previewRappi.resumen.total)} total
            </div>
            <div style={{ fontSize: 12, color: '#7C2D12', marginBottom: 10 }}>
              ⚠️ Las órdenes ya existentes se actualizarán. Las nuevas se agregarán.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleImportarRappi} disabled={importandoRappi} style={{
                padding: '8px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                background: `linear-gradient(135deg,${NARANJA},#C2410C)`, color: 'white', cursor: 'pointer'
              }}>{importandoRappi ? '⏳ Importando...' : '💾 Confirmar e importar'}</button>
              <button onClick={() => setPreviewRappi(null)} style={{
                padding: '8px 16px', border: '2px solid #E5E7EB', borderRadius: 8,
                background: 'white', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontWeight: 600
              }}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* Tabla */}
      {datosRappi && datosRappi.dias.length === 0 && (
        <div style={{ background: 'white', borderRadius: 14, padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
          Sin órdenes Rappi para este rango. Sube el reporte primero.
        </div>
      )}
      {datosRappi && datosRappi.dias.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ background: NARANJA, padding: '11px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>🛵 Rappi vs Sistema — {bodega.nombre}</span>
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>Comisión Rappi estimada ~{Math.round((datosRappi.comision||0.18)*100)}%</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha', '# Órd. Rappi', '# Fact. sistema', 'Dif. órd.', 'Total Rappi', 'Total sistema', 'Dif. monto', 'Neto esperado'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Fecha' ? 'left' : 'right', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datosRappi.dias.map((d, i) => {
                  const alertaOrd = !d.diferencia.cuadra_ordenes && (d.rappi.ordenes > 0 || d.sistema.ordenes > 0);
                  const alertaMonto = !d.diferencia.cuadra_total;
                  const alerta = alertaOrd || alertaMonto;
                  return (
                    <tr key={d.fecha} style={{ borderTop: '1px solid #F3F4F6', background: alerta ? '#FFF7ED' : i%2===0 ? 'white' : '#FAFAFA' }}>
                      <td style={{ padding: '9px 12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {new Date(d.fecha+'T12:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
                        <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>
                          {new Date(d.fecha+'T12:00').toLocaleDateString('es-CO', { weekday: 'long' })}
                        </div>
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: NARANJA }}>
                        {d.rappi.ordenes}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#374151' }}>
                        {d.sistema.ordenes || '—'}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                        {d.diferencia.cuadra_ordenes
                          ? <span style={{ color: '#10B981', fontWeight: 700 }}>✅</span>
                          : <span style={{ color: '#EF4444', fontWeight: 800, fontFamily: 'monospace' }}>{d.diferencia.ordenes > 0 ? '+' : ''}{d.diferencia.ordenes}</span>
                        }
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color: NARANJA, fontWeight: 700 }}>
                        {COP2(d.rappi.total)}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#374151', fontWeight: 700 }}>
                        {d.sistema.total > 0 ? COP2(d.sistema.total) : '—'}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                        <DifBadge val={d.diferencia.total} cuadra={d.diferencia.cuadra_total} />
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: 'monospace', color: '#059669', fontWeight: 800 }}>
                        {COP2(d.neto_esperado)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #E5E7EB', background: '#FFF7ED' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 800, fontSize: 13 }}>Total</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: NARANJA }}>{datosRappi.totales.rappi_ordenes}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#374151' }}>{datosRappi.totales.sis_ordenes}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: datosRappi.totales.rappi_ordenes === datosRappi.totales.sis_ordenes ? '#10B981' : '#EF4444' }}>
                    {datosRappi.totales.rappi_ordenes - datosRappi.totales.sis_ordenes === 0 ? '✅' : datosRappi.totales.rappi_ordenes - datosRappi.totales.sis_ordenes}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: NARANJA }}>{COP2(datosRappi.totales.rappi_total)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#374151' }}>{COP2(datosRappi.totales.sis_total)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}><DifBadge val={datosRappi.totales.sis_total - datosRappi.totales.rappi_total} cuadra={Math.abs(datosRappi.totales.sis_total - datosRappi.totales.rappi_total) < 10000} /></td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#059669' }}>{COP2(datosRappi.totales.neto_esperado)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Panel por PDV ─────────────────────────────────────────
function PanelMediosPDV({ bodega }) {
  const hoy = format(new Date(), 'yyyy-MM-dd');
  const csvDfRef   = useRef();
  const csvRappiRef = useRef();

  const [tab, setTab]                         = useState('datafono');
  const [procesandoDf, setProcesandoDf]       = useState(false);
  const [importandoDf, setImportandoDf]       = useState(false);
  const [previewDf, setPreviewDf]             = useState(null);
  const [datosDf, setDatosDf]                 = useState(null);
  const [cargandoDf, setCargandoDf]           = useState(false);
  const [desdeDf, setDesdeDf]                 = useState(hoy);
  const [hastaDf, setHastaDf]                 = useState(hoy);
  // QR separado
  const csvQrRef                              = useRef();
  const [procesandoQr, setProcesandoQr]       = useState(false);
  const [importandoQr, setImportandoQr]       = useState(false);
  const [previewQr, setPreviewQr]             = useState(null);
  const [datosQr, setDatosQr]                 = useState(null);
  const [cargandoQr, setCargandoQr]           = useState(false);
  const [desdeQr, setDesdeQr]                 = useState(hoy);
  const [hastaQr, setHastaQr]                 = useState(hoy);
  // Cuadre consolidado
  const [datosCuadre, setDatosCuadre]         = useState(null);
  const [cargandoCuadre, setCargandoCuadre]   = useState(false);
  const [desdeCuadre, setDesdeCuadre]         = useState(hoy);
  const [hastaCuadre, setHastaCuadre]         = useState(hoy);
  const [procesandoRappi, setProcesandoRappi] = useState(false);
  const [importandoRappi, setImportandoRappi] = useState(false);
  const [previewRappi, setPreviewRappi]       = useState(null);
  const [datosRappi, setDatosRappi]           = useState(null);
  const [cargandoRappi, setCargandoRappi]     = useState(false);
  const [desdeRappi, setDesdeRappi]           = useState(hoy);
  const [hastaRappi, setHastaRappi]           = useState(hoy);

  const cargarDatafono = () => {
    setCargandoDf(true); setDatosDf(null);
    API.get('/datafono/resumen', { params: { bodega_id: bodega.id, desde: desdeDf, hasta: hastaDf, tipo: 'tarjeta' } })
      .then(r => setDatosDf(r.data))
      .catch(() => toast.error('Error cargando datáfono'))
      .finally(() => setCargandoDf(false));
  };

  const cargarQr = () => {
    setCargandoQr(true); setDatosQr(null);
    API.get('/datafono/resumen', { params: { bodega_id: bodega.id, desde: desdeQr, hasta: hastaQr, tipo: 'qr' } })
      .then(r => setDatosQr(r.data))
      .catch(() => toast.error('Error cargando QR'))
      .finally(() => setCargandoQr(false));
  };

  const cargarCuadre = () => {
    setCargandoCuadre(true); setDatosCuadre(null);
    API.get('/datafono/resumen', { params: { bodega_id: bodega.id, desde: desdeCuadre, hasta: hastaCuadre } })
      .then(r => setDatosCuadre(r.data))
      .catch(() => toast.error('Error cargando cuadre'))
      .finally(() => setCargandoCuadre(false));
  };

  const cargarRappi = () => {
    setCargandoRappi(true); setDatosRappi(null);
    API.get('/rappi/resumen', { params: { bodega_id: bodega.id, desde: desdeRappi, hasta: hastaRappi } })
      .then(r => setDatosRappi(r.data))
      .catch(() => toast.error('Error cargando Rappi'))
      .finally(() => setCargandoRappi(false));
  };

  const handleCSVDatafono = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setProcesandoDf(true); setPreviewDf(null);
    try {
      const texto = await file.text();
      const transacciones = parsearCSVDatafono(texto);
      const resumen = { qr: 0, tarjeta: 0, comision: 0, count: transacciones.length };
      transacciones.forEach(t => {
        if (t.tipo === 'qr') resumen.qr += t.monto; else resumen.tarjeta += t.monto;
        resumen.comision += t.comision;
      });
      setPreviewDf({ transacciones, resumen });
      if (transacciones.length === 0) toast.error('No se encontraron transacciones.');
      else toast.success(`${transacciones.length} transacciones detectadas`);
    } catch(err) { toast.error('Error: ' + err.message); }
    finally { setProcesandoDf(false); e.target.value = ''; }
  };

  const handleImportarDatafono = async () => {
    if (!previewDf?.transacciones?.length) return;
    setImportandoDf(true);
    try {
      const r = await API.post('/datafono/importar', { transacciones: previewDf.transacciones, bodega_id: bodega.id });
      toast.success(`✅ ${r.data.insertados} transacciones importadas`);
      setPreviewDf(null);
      setTimeout(cargarDatafono, 300);
    } catch(e) { toast.error(e.response?.data?.error || 'Error al importar'); }
    finally { setImportandoDf(false); }
  };

  const handleCSVQr = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setProcesandoQr(true); setPreviewQr(null);
    try {
      let todasTransacciones = [];
      for (const file of files) {
        const texto = await file.text();
        const trans = parsearCSVDatafono(texto).filter(t => t.tipo === 'qr');
        todasTransacciones = todasTransacciones.concat(trans);
      }
      // Deduplicar por ID transacción (único real por operación en Davivienda P2M)
      const vistas = new Set();
      const transacciones = todasTransacciones.filter(t => {
        const key = t.id_trx || `${t.fecha}-${t.monto}-${Math.random()}`;
        if (t.id_trx && vistas.has(key)) return false;
        vistas.add(key);
        return true;
      });
      const resumen = { qr: transacciones.reduce((s,t) => s+t.monto, 0), count: transacciones.length, archivos: files.length };
      setPreviewQr({ transacciones, resumen });
      if (transacciones.length === 0) toast.error('No se encontraron transacciones QR/P2M.');
      else toast.success(`${transacciones.length} transacciones QR de ${files.length} archivo(s)`);
    } catch(err) { toast.error('Error: ' + err.message); }
    finally { setProcesandoQr(false); e.target.value = ''; }
  };

  const handleImportarQr = async () => {
    if (!previewQr?.transacciones?.length) return;
    setImportandoQr(true);
    try {
      const r = await API.post('/datafono/importar', { transacciones: previewQr.transacciones, bodega_id: bodega.id });
      toast.success(`✅ ${r.data.insertados} transacciones QR importadas`);
      setPreviewQr(null);
      setTimeout(cargarQr, 300);
    } catch(e) { toast.error(e.response?.data?.error || 'Error al importar'); }
    finally { setImportandoQr(false); }
  };

  const handleExcelRappi = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setProcesandoRappi(true); setPreviewRappi(null);
    try {
      const texto = await file.text();
      const lineas = texto.split('\n').filter(l => l.trim());
      const sep = lineas[0].includes('\t') ? '\t' : ',';
      const headers = lineas[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
      const iOrden    = headers.findIndex(h => /orden/i.test(h));
      const iCliente  = headers.findIndex(h => /cliente/i.test(h));
      const iSucursal = headers.findIndex(h => /sucursal/i.test(h));
      const iFecha    = headers.findIndex(h => /fecha/i.test(h));
      const iMetodo   = headers.findIndex(h => /m.todo|pago/i.test(h));
      const iTotal    = headers.findIndex(h => /total/i.test(h));
      const iEstado   = headers.findIndex(h => /estado/i.test(h));
      const ordenes = [];
      for (let i = 1; i < lineas.length; i++) {
        const cols = lineas[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
        const orden_id = cols[iOrden]?.trim(); if (!orden_id) continue;
        const total = parseFloat(cols[iTotal]) || 0; if (total <= 0) continue;
        ordenes.push({ orden_id, cliente: cols[iCliente]?.replace(' null','').trim()||'', sucursal_rappi: cols[iSucursal]?.trim()||'', fecha: cols[iFecha]?.trim()||'', metodo_pago: cols[iMetodo]?.trim()||'', total, estado: cols[iEstado]?.trim()||'Terminada' });
      }
      setPreviewRappi({ ordenes, resumen: { total: ordenes.reduce((s,o) => s+o.total, 0), count: ordenes.length } });
      if (ordenes.length === 0) toast.error('No se encontraron órdenes.');
      else toast.success(`${ordenes.length} órdenes detectadas`);
    } catch(err) { toast.error('Error: ' + err.message); }
    finally { setProcesandoRappi(false); e.target.value = ''; }
  };

  const handleImportarRappi = async () => {
    if (!previewRappi?.ordenes?.length) return;
    setImportandoRappi(true);
    try {
      const r = await API.post('/rappi/importar', { ordenes: previewRappi.ordenes, bodega_id: bodega.id });
      toast.success(`✅ ${r.data.insertadas} nuevas, ${r.data.actualizadas} actualizadas`);
      setPreviewRappi(null); setTimeout(cargarRappi, 300);
    } catch(e) { toast.error(e.response?.data?.error || 'Error al importar'); }
    finally { setImportandoRappi(false); }
  };

  const RAPPI_BODEGAS = [2, 3, 5];

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 14, background: '#F3F4F6', borderRadius: 8, padding: 3, width: 'fit-content' }}>
        {[{ id: 'cuadre', label: '⚖️ Cuadre' }, { id: 'datafono', label: '💳 Datáfono' }, { id: 'qr', label: '📱 QR / P2M' }, ...(RAPPI_BODEGAS.includes(bodega.id) ? [{ id: 'rappi', label: '🛵 Rappi' }] : [])].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '7px 18px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13,
            fontWeight: tab === t.id ? 700 : 500,
            background: tab === t.id ? 'white' : 'transparent',
            color: tab === t.id ? '#111827' : '#6B7280',
            boxShadow: tab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'cuadre' && (
        <VistaCuadre
          bodega={bodega}
          datos={datosCuadre}
          cargando={cargandoCuadre}
          desde={desdeCuadre} setDesde={setDesdeCuadre}
          hasta={hastaCuadre} setHasta={setHastaCuadre}
          cargar={cargarCuadre}
        />
      )}
      {tab === 'datafono' && (
        <VistaDatafono
          bodega={bodega} datosDf={datosDf} previewDf={previewDf}
          procesandoDf={procesandoDf} importandoDf={importandoDf} cargandoDf={cargandoDf}
          desdeDf={desdeDf} setDesdeDf={setDesdeDf}
          hastaDf={hastaDf} setHastaDf={setHastaDf}
          cargarDatafono={cargarDatafono}
          handleCSVDatafono={handleCSVDatafono}
          handleImportarDatafono={handleImportarDatafono}
          setPreviewDf={setPreviewDf} csvDfRef={csvDfRef}
        />
      )}
      {tab === 'qr' && (
        <VistaDatafono
          bodega={bodega} datosDf={datosQr} previewDf={previewQr}
          procesandoDf={procesandoQr} importandoDf={importandoQr} cargandoDf={cargandoQr}
          desdeDf={desdeQr} setDesdeDf={setDesdeQr}
          hastaDf={hastaQr} setHastaDf={setHastaQr}
          cargarDatafono={cargarQr}
          handleCSVDatafono={handleCSVQr}
          handleImportarDatafono={handleImportarQr}
          setPreviewDf={setPreviewQr} csvDfRef={csvQrRef}
          modoQr={true}
        />
      )}
      {tab === 'rappi' && (
        <VistaRappi
          bodega={bodega} datosRappi={datosRappi} previewRappi={previewRappi}
          procesandoRappi={procesandoRappi} importandoRappi={importandoRappi} cargandoRappi={cargandoRappi}
          desdeRappi={desdeRappi} setDesdeRappi={setDesdeRappi}
          hastaRappi={hastaRappi} setHastaRappi={setHastaRappi}
          cargarRappi={cargarRappi}
          handleExcelRappi={handleExcelRappi}
          handleImportarRappi={handleImportarRappi}
          setPreviewRappi={setPreviewRappi} csvRappiRef={csvRappiRef}
        />
      )}
    </div>
  );
}

// ── Vista Cuadre consolidado ──────────────────────────────
function VistaCuadre({ bodega, datos, cargando, desde, setDesde, hasta, setHasta, cargar }) {
  const COP2 = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n)||0);

  return (
    <div>
      {/* Controles */}
      <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#374151' }}>
          ⚖️ Cuadre medios electrónicos — {bodega.nombre}
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12, background: '#F9FAFB', padding: '8px 12px', borderRadius: 8 }}>
          Compara el total del datáfono (Credibanco + P2M) vs lo facturado en el sistema (tarjeta + QR). Si el total cuadra pero los individuales no, hay una factura mal clasificada.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={st2.label}>Desde</div>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={st2.input} />
          </div>
          <div>
            <div style={st2.label}>Hasta</div>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={st2.input} />
          </div>
          <button onClick={cargar} disabled={cargando} style={{
            padding: '9px 20px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer'
          }}>{cargando ? '⏳' : '🔍 Consultar'}</button>
        </div>
      </div>

      {/* Tabla */}
      {datos && datos.dias.length > 0 && (
        <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ background: '#1a1a2e', padding: '11px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'white', fontWeight: 800, fontSize: 13 }}>⚖️ Cuadre — {bodega.nombre}</span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Si dif. total = ✅ el dinero está, aunque esté mal clasificado</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['Fecha',
                    'Tarjeta banco', 'QR banco', 'Total banco',
                    'Tarjeta sistema', 'QR sistema', 'Total sistema',
                    'Dif. tarjeta', 'Dif. QR', 'Dif. total'
                  ].map(h => (
                    <th key={h} style={{
                      padding: '9px 11px', textAlign: h === 'Fecha' ? 'left' : 'right',
                      fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap',
                      background: ['Total banco','Total sistema','Dif. total'].includes(h) ? '#F3F4F6' : undefined
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datos.dias.map((d, i) => {
                  const totalBanco = d.banco.tarjeta + d.banco.qr;
                  const totalSis   = d.sistema.tarjeta + d.sistema.qr;
                  const difTotal   = totalBanco - totalSis;
                  const cuadraTot  = Math.abs(difTotal) < 5000;
                  const cuadraTar  = Math.abs(d.diferencia.tarjeta) < 5000;
                  const cuadraQr   = Math.abs(d.diferencia.qr) < 2000;
                  const alertaClasif = cuadraTot && (!cuadraTar || !cuadraQr);
                  const alerta = !cuadraTot;
                  return (
                    <tr key={d.fecha} style={{
                      borderTop: '1px solid #F3F4F6',
                      background: alertaClasif ? '#FFFBEB' : alerta ? '#FEF2F2' : i%2===0 ? 'white' : '#FAFAFA'
                    }}>
                      <td style={{ padding: '9px 11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {new Date(d.fecha+'T12:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
                        <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 400 }}>
                          {new Date(d.fecha+'T12:00').toLocaleDateString('es-CO', { weekday: 'long' })}
                        </div>
                        {alertaClasif && (
                          <div style={{ fontSize: 10, color: '#D97706', fontWeight: 700 }}>⚠️ clasif.</div>
                        )}
                      </td>
                      {/* Banco */}
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#2563EB' }}>
                        {d.banco.tarjeta > 0 ? COP2(d.banco.tarjeta) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#0369A1' }}>
                        {d.banco.qr > 0 ? COP2(d.banco.qr) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#111827', background: '#F9FAFB' }}>
                        {COP2(totalBanco)}
                      </td>
                      {/* Sistema */}
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>
                        {d.sistema.tarjeta > 0 ? COP2(d.sistema.tarjeta) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>
                        {d.sistema.qr > 0 ? COP2(d.sistema.qr) : '—'}
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#111827', background: '#F9FAFB' }}>
                        {COP2(totalSis)}
                      </td>
                      {/* Diferencias */}
                      <td style={{ padding: '9px 11px', textAlign: 'right' }}>
                        <DifBadge val={d.diferencia.tarjeta} cuadra={cuadraTar} />
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right' }}>
                        <DifBadge val={d.diferencia.qr} cuadra={cuadraQr} />
                      </td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', background: '#F9FAFB' }}>
                        {cuadraTot
                          ? <span style={{ fontSize: 16 }}>✅</span>
                          : <DifBadge val={difTotal} cuadra={false} />
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {datos.totales && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #E5E7EB', background: '#F3F4F6' }}>
                    <td style={{ padding: '10px 11px', fontWeight: 800, fontSize: 13 }}>Total</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#2563EB' }}>{COP2(datos.totales.banco_tarjeta)}</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#0369A1' }}>{COP2(datos.totales.banco_qr)}</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#111827', background: '#E5E7EB' }}>{COP2(datos.totales.banco_tarjeta + datos.totales.banco_qr)}</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#374151' }}>{COP2(datos.totales.sis_tarjeta)}</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: '#374151' }}>{COP2(datos.totales.sis_qr)}</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: '#111827', background: '#E5E7EB' }}>{COP2(datos.totales.sis_tarjeta + datos.totales.sis_qr)}</td>
                    <td style={{ padding: '10px 11px', textAlign: 'right' }}>
                      <DifBadge val={datos.totales.banco_tarjeta - datos.totales.sis_tarjeta} cuadra={Math.abs(datos.totales.banco_tarjeta - datos.totales.sis_tarjeta) < 10000} />
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right' }}>
                      <DifBadge val={datos.totales.banco_qr - datos.totales.sis_qr} cuadra={Math.abs(datos.totales.banco_qr - datos.totales.sis_qr) < 5000} />
                    </td>
                    <td style={{ padding: '10px 11px', textAlign: 'right', background: '#E5E7EB' }}>
                      {(() => {
                        const dif = (datos.totales.banco_tarjeta + datos.totales.banco_qr) - (datos.totales.sis_tarjeta + datos.totales.sis_qr);
                        return Math.abs(dif) < 10000
                          ? <span style={{ fontSize: 16 }}>✅</span>
                          : <DifBadge val={dif} cuadra={false} />;
                      })()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
      {datos && datos.dias.length === 0 && (
        <div style={{ background: 'white', borderRadius: 14, padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
          Sin datos para este rango. Importa los CSVs de datáfono y QR primero.
        </div>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────
export default function CuadreMediosElectronicos() {
  const { puedeVer } = useAuth();
  const esAdmin = puedeVer('gerencia', 'administracion');
  if (!esAdmin) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
      No tienes permiso para ver este módulo.
    </div>
  );

  const [bodegas, setBodegas] = useState([]);
  const [tabActiva, setTabActiva] = useState(null);

  React.useEffect(() => {
    API.get('/bodegas')
      .then(r => {
        const pvs = r.data.filter(b => b.tipo === 'punto_venta');
        setBodegas(pvs);
        if (pvs.length) setTabActiva(pvs[0].id);
      }).catch(() => {});
  }, []);

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>💳 Cuadre Medios Electrónicos</h1>
        <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>
          Datáfono Credibanco y Rappi vs sistema de facturación — por punto de venta
        </p>
      </div>

      {bodegas.length > 0 && (
        <>
          <div style={{ display: 'flex', borderBottom: '2px solid #E5E7EB', marginBottom: 0 }}>
            {bodegas.map(b => (
              <button key={b.id} onClick={() => setTabActiva(b.id)} style={{
                padding: '10px 22px', border: 'none', cursor: 'pointer', fontSize: 13, background: 'transparent',
                fontWeight: tabActiva === b.id ? 800 : 500,
                color: tabActiva === b.id ? '#111827' : '#6B7280',
                borderBottom: tabActiva === b.id ? '2px solid #111827' : '2px solid transparent',
                marginBottom: -2,
              }}>🏪 {b.nombre}</button>
            ))}
          </div>
          <div style={{ paddingTop: 20 }}>
            {bodegas.map(b => (
              <div key={b.id} style={{ display: tabActiva === b.id ? 'block' : 'none' }}>
                <PanelMediosPDV bodega={b} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function DifBadge({ val, cuadra }) {
  if (val === undefined || val === null) return <span style={{ color: '#D1D5DB' }}>—</span>;
  if (cuadra) return <span style={{ fontSize: 14 }}>✅</span>;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
      background: val < 0 ? '#FEE2E2' : '#FEF3C7',
      color: val < 0 ? '#991B1B' : '#92400E'
    }}>
      {val > 0 ? '+' : ''}{COP(val)}
    </span>
  );
}

function TipoBadge({ tipo }) {
  const map = {
    mastercard: { label: 'Mastercard', bg: '#EEF2FF', color: '#3730A3' },
    visa:       { label: 'Visa',       bg: '#DBEAFE', color: '#1E40AF' },
    amex:       { label: 'Amex',       bg: '#EDE9FE', color: '#5B21B6' },
    qr:         { label: 'QR / Llave', bg: '#E0F2FE', color: '#0369A1' },
  };
  const s = map[tipo] || { label: tipo, bg: '#F3F4F6', color: '#6B7280' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}
